import { createFileRoute, Link, Outlet, redirect } from '@tanstack/react-router';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { loadSession } from '../lib/session';

export const Route = createFileRoute('/admin')({
  beforeLoad: async ({ context }) => {
    const s = await loadSession(context.queryClient);
    if (s.status !== 'signed-in') throw redirect({ to: '/' });
    // Someone without admin rights who follows a link here is told so, not sent home without a word.
    return { adminAllowed: s.user.permissions.includes('admin.manage') };
  },
  component: AdminLayout,
});

const TABS = [
  { to: '/admin', label: 'Overview', exact: true },
  { to: '/admin/users', label: 'Users & access', exact: false },
  { to: '/admin/ai', label: 'AI routing', exact: false },
  { to: '/admin/quality', label: 'AI quality', exact: false },
  { to: '/admin/schema', label: 'Schema', exact: false },
  { to: '/admin/templates', label: 'Templates', exact: false },
  { to: '/admin/report-templates', label: 'Report templates', exact: false },
  { to: '/admin/lists', label: 'Value lists', exact: false },
  { to: '/admin/relations', label: 'Relation types', exact: false },
  { to: '/admin/colors', label: 'Color rules', exact: false },
  { to: '/admin/search', label: 'Search index', exact: false },
  { to: '/admin/matching', label: 'Matching', exact: false },
  { to: '/admin/report-gates', label: 'Report quality gates', exact: false },
  { to: '/admin/audit', label: 'Audit log', exact: false },
  { to: '/admin/exports', label: 'Exports', exact: false },
  { to: '/admin/backups', label: 'Backups', exact: false },
  { to: '/admin/api-keys', label: 'API keys', exact: false },
  { to: '/admin/scrapers', label: 'Website monitors', exact: false },
  { to: '/admin/registers', label: 'Public registers', exact: false },
  { to: '/admin/social', label: 'Social feeds', exact: false },
  { to: '/admin/sources', label: 'Sources', exact: false },
  { to: '/admin/outlets', label: 'Outlets', exact: false },
  { to: '/admin/monitoring', label: 'Monitoring', exact: false },
  { to: '/admin/erasures', label: 'Erasures', exact: false },
  { to: '/admin/help', label: 'Help articles', exact: false },
] as const;

function AdminLayout() {
  const { adminAllowed } = Route.useRouteContext();
  if (!adminAllowed)
    return (
      <>
        <PageHeader title="Admin" />
        <div role="status" className="max-w-xl space-y-3 rounded-lg border bg-surface p-6 text-sm">
          <p>Admin is for administrators. Your account doesn't have admin rights.</p>
          <p className="text-muted-foreground">If you need a setting changed, ask an administrator of this workspace.</p>
          <Button asChild variant="outline" size="sm">
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </>
    );
  return (
    <div className="space-y-6">
      <nav aria-label="Admin" className="flex gap-1 overflow-x-auto border-b">
        {TABS.map(t => (
          <Link
            key={t.to}
            to={t.to}
            activeOptions={{ exact: t.exact }}
            className="-mb-px border-b-2 border-transparent px-3 py-2 text-sm text-muted-foreground hover:text-foreground data-[status=active]:border-foreground data-[status=active]:font-medium data-[status=active]:text-foreground"
          >
            {t.label}
          </Link>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}
